import { FC, useRef } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Check, ChevronsUpDown, Minus, Plus } from 'lucide-react';
import ClassBadge from '../ClassIcon/ClassBadge';
import { hidePanelUntilPlaced, openPanelBesideNav } from './openPanelBesideNav';
import { useGlobalSiteFilter } from '../../custom-hooks/useGlobalSiteFilter';

const studentsLabel = (count: number): string => `${count} ${count === 1 ? 'student' : 'students'}`;

const GlobalSiteFilter: FC = () => {
  const { sites, selectedSites, selectedStudentCount, select } = useGlobalSiteFilter();
  const overlay = useRef<OverlayPanel>(null);
  const triggerId = 'siteSelectorBtn';

  const isSingle = selectedSites.length === 1;
  const summary = isSingle ? selectedSites[0].name : `${selectedSites.length} Sites selected`;

  const selectOnly = (siteId: string) => select([siteId]);

  const toggle = (siteId: string) => {
    const isSelected = selectedSites.some(site => site.id === siteId);

    if (isSelected && selectedSites.length === 1) return;

    select(
      isSelected
        ? selectedSites.filter(site => site.id !== siteId).map(site => site.id)
        : [...selectedSites.map(site => site.id), siteId],
    );
  };

  if (!sites.length) {
    return (
      <div className="nc_class-filter nc_class-filter--empty" data-testid="globalSiteFilterEmpty">
        <ClassBadge variant="site" />
        <span className="nc_class-filter__name">No sites yet</span>
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        id={triggerId}
        className="nc_class-filter"
        data-testid="globalSiteFilter"
        aria-haspopup="dialog"
        aria-label={`Site filter: ${selectedSites.map(site => site.name).join(', ')}`}
        onClick={event => overlay.current?.toggle(event)}
      >
        <ClassBadge variant={isSingle ? 'site' : 'multi'} />
        <span className="nc_class-filter__name">{summary}</span>
        <span className="nc_class-filter__count">{studentsLabel(selectedStudentCount)}</span>
        <ChevronsUpDown size={16} className="nc_class-filter__chevron" aria-hidden="true" />
      </button>

      <OverlayPanel
        ref={overlay}
        className="nc_class-filter__panel"
        onShow={() => openPanelBesideNav(overlay, triggerId)}
        onHide={() => hidePanelUntilPlaced(overlay)}
      >
        <ul className="nc_class-filter__options">
          {sites.map(site => {
            const isSelected = selectedSites.some(selected => selected.id === site.id);
            const isLocked = isSelected && selectedSites.length === 1;

            return (
              <li
                key={site.id}
                className={`nc_class-filter__row ${isSelected ? 'nc_class-filter__row--selected' : ''}`}
              >
                <button
                  type="button"
                  className="nc_class-filter__row-name"
                  aria-pressed={isSelected}
                  onClick={() => selectOnly(site.id)}
                >
                  {isSelected && <Check size={16} className="nc_class-filter__check" aria-hidden="true" />}
                  <span className="nc_class-filter__option-name">{site.name}</span>
                </button>

                <button
                  type="button"
                  className={`nc_class-filter__action ${isLocked ? 'nc_class-filter__action--locked' : ''}`}
                  aria-disabled={isLocked}
                  aria-label={`${isSelected ? 'Remove' : 'Add'} ${site.name}`}
                  onClick={() => toggle(site.id)}
                >
                  {isSelected ? <Minus size={16} aria-hidden="true" /> : <Plus size={16} aria-hidden="true" />}
                </button>
              </li>
            );
          })}
        </ul>

        <div className="nc_class-filter__footer">
          <span />
          <button type="button" className="nc_class-filter__done" onClick={() => overlay.current?.hide()}>
            Done
          </button>
        </div>
      </OverlayPanel>
    </>
  );
};

export default GlobalSiteFilter;
